import { useEffect, useState, useMemo } from "react";
import { Card, Title, Text } from "@tremor/react";
import ThickDonut from "./ThickDonut";
import { useFilterContext } from "../context/FilterContext";
import { apiFetch } from "../api/client";
import {
  agruparPorCategoria,
  totalCategorias,
  formatCompactMoney,
  type CategoriaDato,
} from "../lib/distribucionCategoria";

interface ProductoKPI {
  producto: string;
  canal?: string;
  categoria?: string;
  venta_ytd: number;
  venta_mes_actual?: number | null;
  pct_total?: number;
}
interface VentasPorProductoResponse {
  restaurant_id: string;
  anio: number;
  venta_ytd: number;
  productos: ProductoKPI[];
}

const MESES_ES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

export default function DistribucionCategoriaSection() {
  const { selectedRestaurantSlug, mesSel } = useFilterContext();
  const [data, setData] = useState<VentasPorProductoResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedRestaurantSlug) return;
    const cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({
      restaurant_id: selectedRestaurantSlug,
      limit: "100",
    });
    if (mesSel) {
      params.set("anio", String(mesSel.anio));
      params.set("mm", String(mesSel.mm));
    }
    apiFetch(`/api/comercial/ventas-por-producto?${params}`)
      .then((res) => {
        if (!res.ok) {
          if (res.status === 404) { setError("SIN_DATOS"); setLoading(false); return; }
          throw new Error(`HTTP ${res.status}`);
        }
        return res.json();
      })
      .then((json) => { if (!cancelled) { setData(json); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(String(e.message)); setLoading(false); } });
  }, [selectedRestaurantSlug, mesSel]);

  // Modo: si hay mes seleccionado, la distribución es SOLO ese mes (no YTD).
  const hayMes = !!mesSel;
  const modo = hayMes ? "mes" : "ytd";
  const periodoLabel = mesSel
    ? `${MESES_ES[mesSel.mm - 1]} ${mesSel.anio}`
    : "Periodo";

  // Agrupación por categoría (usa el mes filtrado si hay mes seleccionado).
  const categorias: CategoriaDato[] = useMemo(() => {
    const productos = (data?.productos ?? []).map((p) => ({
      producto: p.producto,
      canal: p.canal,
      categoria: p.categoria,
      venta_ytd: p.venta_ytd,
      venta_mes: p.venta_mes_actual ?? 0,
    }));
    return agruparPorCategoria(productos, modo);
  }, [data, modo]);

  if (loading) {
    return (
      <Card className="!p-3">
        <Text className="text-tremor-content">Cargando…</Text>
      </Card>
    );
  }
  if (error === "SIN_DATOS" || categorias.length === 0) {
    return (
      <Card className="!p-3">
        <Text className="text-tremor-content">Sin datos para {periodoLabel}.</Text>
      </Card>
    );
  }

  const total = totalCategorias(categorias);
  const donutData = categorias.map((c) => ({ name: c.name, value: c.value }));

  return (
    <Card className="!p-3">
      <Title className="!text-base">🍩 Distribución por categoría</Title>
      <Text className="!text-xs !mt-0.5">{periodoLabel}</Text>
      <div className="grid grid-cols-2 gap-2 items-center mt-2">
        {/* Dona (misma estructura que "Mix por canal": ThickDonut directo en el grid) */}
        <ThickDonut
          data={donutData}
          colors={categorias.map((c) => c.color)}
          centerLabel={formatCompactMoney(total)}
          centerSub="Total"
          height={160}
          innerRadius={58}
          outerRadius={100}
        />
        {/* Leyenda estructurada: color · categoría · % · valor */}
        <div className="space-y-1">
          {categorias.map((d) => {
            const pct = total > 0 ? (d.value / total) * 100 : 0;
            return (
              <div key={d.name} className="flex items-center gap-2 text-xs">
                <span
                  className="inline-block w-2.5 h-2.5 rounded-sm shrink-0"
                  style={{ backgroundColor: d.color }}
                />
                <span className="flex-1 truncate text-tremor-content-emphasis">
                  {d.name}
                </span>
                <span className="tabular-nums font-medium w-10 text-right">
                  {pct.toFixed(1)}%
                </span>
                <span className="tabular-nums w-14 text-right text-tremor-content">
                  {formatCompactMoney(d.value)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}
